// ======================================================
// utils/storage/authStorage.js
// SINGLE SOURCE OF TRUTH
// ======================================================

import Cookies from "js-cookie";

// ======================================================
// STORAGE KEYS
// ======================================================

export const STORAGE_KEYS = {
  ACCESS_TOKEN: "accessToken",
  REFRESH_TOKEN: "refreshToken",
  USER: "patientPersona",
};

// ======================================================
// COOKIE OPTIONS
// ======================================================

const COOKIE_OPTIONS = {
  expires: 7,
  secure: false,
  sameSite: "Lax",
};

// ======================================================
// COMMON STORAGE
// ======================================================

const setStorage = (
  key,
  value,
  options = COOKIE_OPTIONS
) => {
  if (
    value === undefined ||
    value === null
  ) {
    return;
  }

  const data =
    typeof value === "string"
      ? value
      : JSON.stringify(value);

  Cookies.set(
    key,
    data,
    options
  );

  localStorage.setItem(
    key,
    data
  );
};

const getStorage = (
  key,
  parse = false
) => {
  try {
    const value =
      Cookies.get(key) ||
      localStorage.getItem(key);

    if (!value) {
      return parse
        ? {}
        : null;
    }

    return parse
      ? JSON.parse(value)
      : value;
  } catch (error) {
    console.error(
      "getStorage error:",
      error
    );

    return parse
      ? {}
      : null;
  }
};

const removeStorage = (
  key
) => {
  Cookies.remove(key);

  localStorage.removeItem(key);
};

// ======================================================
// GETTERS
// ======================================================

export const getAccessToken =
  () =>
    getStorage(
      STORAGE_KEYS.ACCESS_TOKEN
    );
export const setAccessToken =
  (value) =>
    setStorage(
      STORAGE_KEYS.ACCESS_TOKEN,
      value
    );

export const getRefreshToken =
  () =>
    getStorage(
      STORAGE_KEYS.REFRESH_TOKEN
    );

// export const getPatientPersona =
//   () =>
//     getStorage(
//       STORAGE_KEYS.USER,
//       true
//     );
export const getPatientPersona = () => {
  const value = getStorage(STORAGE_KEYS.USER, true);

  console.log("getPatientPersona()", value);

  return value;
};

// ======================================================
// STORE SESSION
// SUPPORTS PARTIAL UPDATE
// ======================================================

export const storePatientSession = (
  response = {}
) => {
    console.log("storePatientSession response", response);
  // ==========================================
  // TOKENS
  // ==========================================

  if (response?.accessToken) {
    setStorage(
      STORAGE_KEYS.ACCESS_TOKEN,
      response.accessToken
    );
  }

  if (response?.refreshToken) {
    setStorage(
      STORAGE_KEYS.REFRESH_TOKEN,
      response.refreshToken,
      {
        ...COOKIE_OPTIONS,
        expires: 30,
      }
    );
  }

  // ==========================================
  // EXISTING USER
  // ==========================================

  const existingUser =
    getPatientPersona();

  // ==========================================
  // SAFE MERGE
  // ==========================================

  const userPayload = {
    ...existingUser,

    id:
      response?.id ??
      existingUser?.id,
    patientId:response?.patientId??
    existingUser.patientId,
    fullName:
      response?.fullName ??
      response?.userName ??
      existingUser?.fullName,

    name:
      response?.fullName ??
      response?.userName ??
      existingUser?.name,

    mobile:
      response?.mobile ??
      response?.mobileNumber ??
      existingUser?.mobile,

    mobileNumber:
      response?.mobileNumber ??
      response?.mobile ??
      existingUser?.mobileNumber,

    email:
      response?.email ??
      existingUser?.email,

    dob:
      response?.dob ??
      existingUser?.dob,

    ageGroup:
      response?.ageGroup ??
      existingUser?.ageGroup,

    gender:
      response?.gender ??
      existingUser?.gender ?? "female",

    maritalStatus:
      response?.maritalStatus ??
      existingUser?.maritalStatus,

    occupation:
      response?.occupation ??
      existingUser?.occupation,

    height:
      response?.height ??
      existingUser?.heightIn,

    weight:
      response?.weight ??
      existingUser?.weightIn,

    orgName:
      response?.orgName ??
      existingUser?.orgName,

    orgId:
      response?.orgId ??
      existingUser?.orgId,

    isVerified:
      typeof response?.isVerified ===
      "boolean"
        ? response.isVerified
        : existingUser?.isVerified,

    mobileVerified:
      typeof response?.mobileVerified ===
      "boolean"
        ? response.mobileVerified
        : existingUser?.mobileVerified,

    emailVerified:
      typeof response?.emailVerified ===
      "boolean"
        ? response.emailVerified
        : existingUser?.emailVerified,

    role:
      response?.role ??
      existingUser?.role ??
      "PATIENT",

    isLoggedIn: true,

    loginType:
      response?.loginType ??
      existingUser?.loginType ??
      "MOBILE",

    updatedAt: Date.now(),
  };

  // ==========================================
  // STORE USER
  // ==========================================

  setStorage(
    STORAGE_KEYS.USER,
    userPayload
  );
  

  return userPayload;
};

// ======================================================
// UPDATE USER
// ======================================================

export const updatePatientPersona = (
  payload = {}
) => {
  return storePatientSession(
    payload
  );
};

// ======================================================
// CHECK VERIFIED MOBILE
// ======================================================

export const isMobileVerified = (
  mobile = ""
) => {
  const user =
    getPatientPersona();
  // console.log("user, mobile", user, mobile);
  if (
    !user ||
    !mobile
  ) {
    return false;
  }

  return (
    (
      user?.mobile === mobile ||
      user?.mobileNumber ===
        mobile
    ) &&
    (
      user?.isVerified 
    )
  );
};

// ======================================================
// AUTH CHECK
// ======================================================

export const isAuthenticated =
  () => {
    const user =
      getPatientPersona();

    return !!(
      user &&
      Object.keys(user)
        .length > 0
    );
  };

// ======================================================
// CLEAR
// ======================================================

export const clearAuthStorage =
  () => {
    removeStorage(
      STORAGE_KEYS.ACCESS_TOKEN
    );

    removeStorage(
      STORAGE_KEYS.REFRESH_TOKEN
    );

    removeStorage(
      STORAGE_KEYS.USER
    );
  };

// ======================================================
// DEFAULT EXPORT
// ======================================================

const authStorage = {
  STORAGE_KEYS,

  getAccessToken,
  getRefreshToken,
  setAccessToken,
  getPatientPersona,

  storePatientSession,
  updatePatientPersona,

  isMobileVerified,
  isAuthenticated,

  clearAuthStorage,
};

export default authStorage;